/* @flow */
import { Map, fromJS } from 'immutable';
import { isImmutable } from 'trustar/utils/utils.js';
import {
  NONE,
  READ_ONLY,
  READ_CREATE,
  READ_WRITE,
} from './types';

/* Util to determine if an enclave is private or not, used for filtering */
export const isPrivateEnclave = (enclave: Object): boolean => {
  return enclave.label === 'INTERNAL';
};

/* NOTE: These methods are mostly utility helpers for the company, user, and enclave forms,
 * related to managing setting and changing enclave permissions visibilty.
 */

// Convert enclave to plain JS read/create/update permissions
const getPlainPermissions = (enclave: Map<any, any>): Object => {
  const read = enclave.get('read');
  const create = enclave.get('create');
  const update = enclave.get('update');
  return { read, create, update };
};

export const filterNoAccessEnclaves = (enclave: Map<any, any>): boolean => {
  return determineAccessType(enclave).get('value') === READ_WRITE;
};

/* Determine appropriate access option for enclave permissions field based on
 * selected read/create/update combination
 */
export const determineAccessType = (enclave: Map<any, any>): Object => {
  const { read, create, update } = getPlainPermissions(enclave);
  if (update) return fromJS({ value: READ_WRITE, label: 'Full Access' });
  if (create) return fromJS({ value: READ_CREATE, label: 'Submission Access' });
  if (read) return fromJS({ value: READ_ONLY, label: 'View Only' });
  return fromJS({ value: NONE, label: 'No Access' });
};

const createPermissions = (read, create, update) => {
  return Map({ read, create, update });
};

/* Given an access type, NONE, READ_ONLY, READ_WRITE, return the
 * appropriate set of enclave read/create/update permissions
 */
export const accessTypeToPermissions = (accessType: Object): Object => {
  const { value } = accessType;
  switch(value) {
  case READ_ONLY:
    return createPermissions(true, false, false);
  case READ_CREATE:
    return createPermissions(true, true, false);
  case READ_WRITE:
    return createPermissions(true, true, true);
  case NONE:
  default:
    return createPermissions(false, false, false);
  }
};

/* Determine availabe options given a company enclave.
 * This method will add disabled fields to unavailable options.
 */
const createAllowedOptions = (enclave: Map<any, any>) => {
  const { read, create, update } = getPlainPermissions(enclave);

  let options;
  const accessOptionsMap = fromJS({
    [NONE]: { value: NONE, label: 'No Access' },
    [READ_ONLY]: { value: READ_ONLY, label: 'View Only' },
    [READ_CREATE]: { value: READ_CREATE, label: 'Submission Access' },
    [READ_WRITE]: { value: READ_WRITE, label: 'Full Access' },
  });
  if (update) {
    options = accessOptionsMap;
  } else if (create) {
    options = accessOptionsMap
      .update(READ_WRITE, o => o.set('disabled', true));
  } else if (read) {
    options = accessOptionsMap
      .update(READ_WRITE, o => o.set('disabled', true))
      .update(READ_CREATE, o => o.set('disabled', true));
  } else {
    options = accessOptionsMap
      .update(READ_ONLY, o => o.set('disabled', true))
      .update(READ_CREATE, o => o.set('disabled', true))
      .update(READ_WRITE, o => o.set('disabled', true));
  }

  return options.toList();
};

/* This method sets the current access type for an enclave and
 * determines the available options that can be selected for access.
 * This is used by the Users form.
 */
export const attachAllowedAccessOptions = (enclave: Map<any, any>): Map<any, any> => {
  const maximumAccess = determineAccessType(enclave);
  const accessOptions = createAllowedOptions(enclave);
  return Map(enclave.toObject()).merge(Map({ maximumAccess, accessOptions }));
};

/* Used by the Companies Form for Admins when selecting enclave permissions level
 * for a company. We allow the Admin to select an access level beyond
 * the enclaves set access, and this method determines if the Admin's
 * selection exceeds the given permissions for the selected enclave.
 */
export const hasExcessivePermissions = (enclave: any, companyEnclave: any): boolean => {
  const selectedAccessType = enclave.getIn(['maximumAccess', 'value']);
  const allowedAccessType = companyEnclave.get('maximumAccess');
  if (selectedAccessType === READ_WRITE && allowedAccessType !== READ_WRITE) {
    return true;
  }
  if (selectedAccessType === READ_CREATE && allowedAccessType === READ_ONLY || allowedAccessType === NONE) {
    return true;
  }
  if (selectedAccessType === READ_ONLY && allowedAccessType === NONE) {
    return true;
  }
  if (selectedAccessType !== NONE && allowedAccessType === NONE) {
    return true;
  }
  return false;
};

/**
 * Checks if the provided enclave is of type "Community".
 */
export const isCommunityEnclave = (enclave: any) => {
  if (isImmutable(enclave)) {
    return enclave.get('label', '') === 'COMMUNITY';
  }

  return enclave.label === 'COMMUNITY';
};



// WEBPACK FOOTER //
// ./src/app/modules/enclave/utils.js